import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Globe, Plus, Trash2, Archive, CheckCircle2, Calendar } from "lucide-react";
import api from "@/lib/api";
import { STATUSES, statusColor } from "@/components/admin/status";

const Section = ({ title, children, testid }) => (
  <div className="rounded-2xl border border-[#2E1B16]/10 bg-white p-6" data-testid={testid}>
    <h3 className="label-eyebrow text-[#2E1B16]/45 mb-4">{title}</h3>
    {children}
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-[#2E1B16]/6 last:border-0">
    <span className="text-[#2E1B16]/50">{label}</span>
    <span className="text-[#2E1B16] font-medium text-right">{value || "—"}</span>
  </div>
);
const fmt = (s) => (s ? new Date(s).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const LeadProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");
  const [task, setTask] = useState("");

  const load = () => api.get(`/admin/leads/${id}`).then((r) => setLead(r.data)).catch(() => navigate("/admin"));
  useEffect(() => { load(); }, [id]);

  if (!lead) return <div className="min-h-screen grid place-items-center bg-[#FAF7F2] text-[#2E1B16]/50 font-body">Loading…</div>;

  const patch = async (body) => { const { data } = await api.patch(`/admin/leads/${id}`, body); setLead(data); };
  const addNote = async () => { if (!note.trim()) return; const { data } = await api.post(`/admin/leads/${id}/notes`, { text: note }); setLead(data); setNote(""); };
  const addTask = async () => { if (!task.trim()) return; const { data } = await api.post(`/admin/leads/${id}/tasks`, { title: task }); setLead(data); setTask(""); };
  const toggleTask = async (tid) => { const { data } = await api.patch(`/admin/leads/${id}/tasks/${tid}`); setLead(data); };
  const del = async () => { if (window.confirm("Delete this lead permanently?")) { await api.delete(`/admin/leads/${id}`); navigate("/admin"); } };
  const web = lead.website ? (lead.website.startsWith("http") ? lead.website : `https://${lead.website}`) : null;

  const Action = ({ icon: Icon, label, ...props }) => (
    <button {...props} className="flex items-center gap-2 rounded-full border border-[#2E1B16]/15 px-4 py-2 text-sm font-semibold hover:border-[#38BDF8] hover:text-[#0b6f96] transition-colors">
      <Icon size={15} />{label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-body text-[#2E1B16]" data-testid="lead-profile">
      <header className="sticky top-0 z-30 glass-nav border-b border-[#2E1B16]/10">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-semibold text-[#2E1B16]/60 hover:text-[#2E1B16]" data-testid="back-to-centre"><ArrowLeft size={16} /> Lead Centre</Link>
          <select value={lead.status} onChange={(e) => patch({ status: e.target.value })} data-testid="profile-status"
            className={`text-sm font-semibold rounded-full border px-3 py-1.5 focus:outline-none cursor-pointer ${statusColor(lead.status)}`}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="label-eyebrow text-[#38BDF8]">{lead.form_type === "strategy_call" ? "Strategy Call" : "Website Enquiry"}</p>
            <h1 className="font-display text-4xl tracking-tight mt-2">{lead.full_name}</h1>
            <p className="text-[#2E1B16]/55 mt-1">{lead.business_name || "—"}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2.5 mb-8">
          <Action icon={Phone} label="Call" as="a" onClick={() => lead.phone && (window.location.href = `tel:${lead.phone}`)} data-testid="action-call" />
          <Action icon={Mail} label="Email" onClick={() => window.location.href = `mailto:${lead.email}`} data-testid="action-email" />
          {web ? <Action icon={Globe} label="Website" onClick={() => window.open(web, "_blank", "noopener")} data-testid="action-website" />
               : <Action icon={Globe} label="Website" disabled onClick={() => {}} data-testid="action-website" />}
          {lead.base44?.calendar_event_link && <Action icon={Calendar} label="Open Calendar" onClick={() => window.open(lead.base44.calendar_event_link, "_blank", "noopener")} data-testid="action-calendar" />}
          <Action icon={Archive} label="Archive" onClick={() => patch({ status: "On Hold" })} data-testid="action-archive" />
          <button onClick={del} data-testid="action-delete" className="flex items-center gap-2 rounded-full border border-rose-300 text-rose-600 px-4 py-2 text-sm font-semibold hover:bg-rose-50 transition-colors"><Trash2 size={15} />Delete</button>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="space-y-5">
            <Section title="Business & Contact" testid="section-contact">
              <Row label="Business" value={lead.business_name} />
              <Row label="Email" value={lead.email} />
              <Row label="Phone" value={lead.phone} />
              <Row label="Website" value={lead.website} />
              <Row label="Industry" value={lead.industry} />
              <Row label="Service" value={lead.service} />
              <Row label="Budget" value={lead.budget} />
              <Row label="Source" value={lead.source} />
              <Row label="Submitted" value={fmt(lead.created_at)} />
              <Row label="Assigned to" value={lead.assigned_to} />
              <Row label="Last contacted" value={fmt(lead.last_contacted)} />
            </Section>
            {lead.form_type === "strategy_call" && (
              <Section title="Strategy Call" testid="section-meeting">
                <Row label="Requested date" value={lead.preferred_date} />
                <Row label="Requested time" value={lead.preferred_time} />
                <Row label="Calendar event" value={lead.base44?.calendar_event_link ? "Linked" : "—"} />
              </Section>
            )}
            <Section title="Original Message" testid="section-message">
              <p className="text-sm text-[#2E1B16]/75 whitespace-pre-wrap leading-relaxed">{lead.message || "No message provided."}</p>
            </Section>
          </div>

          <div className="space-y-5">
            <Section title="Internal Notes" testid="section-notes">
              <div className="flex gap-2 mb-4">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="note-input"
                  className="flex-1 bg-transparent border-b border-[#2E1B16]/20 py-2 text-sm focus:border-[#38BDF8] focus:outline-none" />
                <button onClick={addNote} data-testid="note-add" className="rounded-full bg-[#2E1B16] text-[#FAF7F2] px-3 grid place-items-center hover:bg-[#38BDF8] hover:text-[#2E1B16] transition-colors"><Plus size={16} /></button>
              </div>
              <div className="space-y-3">
                {(lead.notes || []).slice().reverse().map((n, i) => (
                  <div key={i} className="rounded-xl bg-[#F2EFE9] p-3"><p className="text-sm">{n.text}</p><p className="text-[11px] text-[#2E1B16]/45 mt-1">{n.by} · {fmt(n.at)}</p></div>
                ))}
                {(!lead.notes || lead.notes.length === 0) && <p className="text-sm text-[#2E1B16]/45">No notes yet.</p>}
              </div>
            </Section>
            <Section title="Tasks" testid="section-tasks">
              <div className="flex gap-2 mb-4">
                <input value={task} onChange={(e) => setTask(e.target.value)} placeholder="Add a follow-up task…" data-testid="task-input"
                  className="flex-1 bg-transparent border-b border-[#2E1B16]/20 py-2 text-sm focus:border-[#38BDF8] focus:outline-none" />
                <button onClick={addTask} data-testid="task-add" className="rounded-full bg-[#2E1B16] text-[#FAF7F2] px-3 grid place-items-center hover:bg-[#38BDF8] hover:text-[#2E1B16] transition-colors"><Plus size={16} /></button>
              </div>
              <div className="space-y-2">
                {(lead.tasks || []).map((t) => (
                  <button key={t.id} onClick={() => toggleTask(t.id)} data-testid={`task-toggle-${t.id}`} className="w-full flex items-center gap-2.5 text-left text-sm p-2 rounded-lg hover:bg-[#F2EFE9]">
                    <CheckCircle2 size={16} className={t.done ? "text-[#38BDF8]" : "text-[#2E1B16]/25"} />
                    <span className={t.done ? "line-through text-[#2E1B16]/45" : ""}>{t.title}</span>
                  </button>
                ))}
                {(!lead.tasks || lead.tasks.length === 0) && <p className="text-sm text-[#2E1B16]/45">No tasks yet.</p>}
              </div>
            </Section>
          </div>

          <div className="space-y-5">
            <Section title="Timeline" testid="section-timeline">
              <div className="space-y-4">
                {(lead.timeline || []).slice().reverse().map((e, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-[#38BDF8] shrink-0" />
                    <div><p className="text-sm">{e.text}</p><p className="text-[11px] text-[#2E1B16]/45">{fmt(e.at)}</p></div>
                  </div>
                ))}
              </div>
            </Section>
            <Section title="Coming soon" testid="section-future">
              <p className="text-sm text-[#2E1B16]/45 leading-relaxed">Proposals, invoices, email &amp; SMS history, AI summaries and lead scoring attach here. The data model is ready for them.</p>
            </Section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LeadProfile;
